/**
 * Role Prioritisation, section 01 — where we stand.
 *
 * The team spends 100 % of its committed hours on the open portfolio, so the interesting numbers
 * are the DELTAS to the effort-model minimum: how many more roles this mix could handle, how
 * much headroom sits above the model minimum, and which cluster the headroom's high-priority
 * work is landing in. The strip now reads:
 *
 *   Open roles · Roles per FTE · Headroom % · Headroom h · Top-prio hotspot
 *
 * The grey subtitle on each tile names the same delta so the whole strip is comparable — how
 * many MORE roles fit, how many MORE roles per FTE, how much of the headroom % / h lands on
 * AAA+A, which cluster carries the most high-priority load.
 *
 * The bar UNDER the strip fills 100 % of its own width with the priority distribution of the
 * open portfolio (share of demand by tier). It carries no capacity marker and no uplift overlay.
 * Model coverage % (the old "Utilisation" figure) still lives in the callout below, with the
 * status badge; presenting it as the PRIMARY figure was reported as misleading (53 % reads
 * "half-idle" — the team is not).
 *
 * The blue absorbs / trade-offs strip and the Add-roles scenario inputs sit inside a collapsed
 * `<details>` at the bottom. Both are worth having, neither is the reason someone opens the tab —
 * "Where we stand" should show what stands, then let a lead ask what would change on click.
 */
import { cn } from '../../../../../shared';
import {
  RATINGS,
  type EffortModel,
  type Portfolio,
  type Rating,
} from '../../../types/rolePrioritisation';
import {
  absorbable,
  fteLabel,
  hoursLabel,
  pctLabel,
  tradeOffs,
  type CompanyStats,
} from '../../../utils/rolePrioritisation';
import {
  Card,
  Fact,
  FactStrip,
  FactValue,
  RatingBadge,
  SectionHead,
  hoursText,
} from './prioritisationUi';
import { CapacityBar, ServiceLevelLine } from './CapacityBar';

/** Per-cluster role breakdown, passed through from the live portfolio when Airtable answers. */
export interface ClusterPortfolio {
  key: string;
  roles: Portfolio;
}

export function PortfolioOverview({
  stats,
  model,
  added,
  openRolesTotal,
  onAddedChange,
  supportSummary,
  clusterPortfolios,
}: {
  stats: CompanyStats;
  model: EffortModel;
  added: Portfolio;
  /** Every open role, rated or not — this must match what Airtable reports. */
  openRolesTotal: number;
  onAddedChange: (next: Portfolio) => void;
  supportSummary: string | null;
  /** Per-cluster role breakdown, when live data is on. Powers the "Top-prio hotspot" tile. */
  clusterPortfolios?: readonly ClusterPortfolio[];
}) {
  const over = stats.gap > 0.01;

  function setAdded(rating: Rating, raw: string) {
    const v = parseInt(raw, 10);
    onAddedChange({ ...added, [rating]: Number.isNaN(v) || v < 0 ? 0 : v });
  }

  // Reusable across three tiles: capacity for MORE roles at the current mix.
  const capacityMultiplier =
    stats.demand > 0.001 && stats.committed > 0 ? stats.committed / stats.demand : null;
  const extraRoles =
    capacityMultiplier != null
      ? Math.max(0, Math.round(openRolesTotal * (capacityMultiplier - 1)))
      : null;
  const maxRolesPerFte =
    capacityMultiplier != null && stats.fte > 0
      ? (openRolesTotal / stats.fte) * capacityMultiplier
      : null;
  const headroomHours = Math.max(0, -stats.gap);
  const headroomPct =
    stats.committed > 0 ? Math.max(0, 1 - stats.demand / stats.committed) * 100 : null;

  return (
    <section>
      <SectionHead
        number="01"
        title="Where we stand"
        aside="Hours per week · demand against committed capacity"
      />
      <Card>
        <div className="grid grid-cols-4 max-[720px]:grid-cols-2">
          <Kpi label="Open roles" testId="kpi-roles" value={String(openRolesTotal)}>
            {extraRoles != null && extraRoles > 0
              ? `room for ${extraRoles} more at this mix`
              : 'at capacity'}
          </Kpi>
          <Kpi
            label="Roles per FTE"
            testId="kpi-fte"
            value={stats.fte > 0 ? fteLabel(openRolesTotal / stats.fte) : '—'}
          >
            {maxRolesPerFte != null && maxRolesPerFte > openRolesTotal / stats.fte + 0.05
              ? `up to ${fteLabel(maxRolesPerFte)} at this mix`
              : 'at capacity'}
          </Kpi>
          {/*
            ⚠️ ONE headroom tile, not two. It used to be a percentage AND the hours, which is the
            same fact said twice and cost a fifth of the strip — and neither of them answered the
            question, because at 0% both read "—" and nothing on the page said whether that was
            tight or badly over. The BAR below answers it now; this tile carries the number.
          */}
          <Kpi
            label={over ? 'Over capacity' : 'Headroom'}
            testId="kpi-headroom-h"
            value={hoursLabel(over ? stats.gap : headroomHours)}
            unit="h"
          >
            {over
              ? 'every role below its minimum'
              : headroomHours > 0.05
                ? `${pctLabel(headroomPct)} of the week`
                : 'nothing spare'}
          </Kpi>
          <TopPrioKpi clusters={clusterPortfolios} />
        </div>

        <div className="border-t border-solid border-pearl px-4 pt-4 pb-4">
          <CapacityBar stats={stats} model={model} />
          <ServiceLevelLine stats={stats} />
        </div>

        {/*
          The blue Absorbs / trade-offs strip and the Add-roles scenario inputs are one action away:
          both are worth having, neither is the reason someone opens the tab. `<details>` is native
          and keeps the DOM in place so testids still resolve (`portfolio-facts` still queryable).
        */}
        <details className="group border-t border-solid border-pearl">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-xs font-medium text-iron hover:bg-cotton">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rotate-0 border-r-[1.5px] border-b-[1.5px] border-iron transition-transform duration-150 group-open:rotate-45"
              style={{ transform: 'rotate(-45deg)' }}
            />
            <span>Scenario controls · what a headroom closes and what an added role costs</span>
          </summary>

          <FactStrip
            tone={over ? 'red' : 'blue'}
            className="border-t border-solid border-pearl"
            testId="portfolio-facts"
          >
            {over ? (
              <>
                <Fact>
                  Over by <FactValue>{hoursText(stats.gap)}</FactValue>
                </Fact>
                <Fact>
                  Closed by any one of{' '}
                  {tradeOffs(stats.gap, model).map((o, i) => (
                    <span key={o.id}>
                      {i > 0 ? ' · ' : ''}
                      <FactValue>{o.roles}</FactValue> {o.label}
                    </span>
                  ))}
                </Fact>
              </>
            ) : (
              <>
                <Fact>
                  Headroom <FactValue>{hoursText(-stats.gap)}</FactValue>
                </Fact>
                <Fact>
                  Absorbs{' '}
                  {absorbable(-stats.gap, model).map((e, i) => (
                    <span key={e.rating}>
                      {i > 0 ? ' · ' : ''}
                      <FactValue>{e.roles}</FactValue> {e.rating}
                    </span>
                  ))}
                </Fact>
              </>
            )}
            <Fact>
              AAA+A mix <FactValue>{pctLabel(stats.topTierSharePct)}</FactValue> of capacity
              {stats.guardrailBreached ? ' — above the 70% guardrail' : ' · guardrail 70%'}
            </Fact>
            {supportSummary ? <Fact>In effect {supportSummary}</Fact> : null}
          </FactStrip>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-solid border-pearl px-4 py-2 text-xs text-iron">
            <span className="font-semibold text-asphalt">Add roles</span>
            {RATINGS.map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5">
                <RatingBadge rating={r} />
                <input
                  type="number"
                  min={0}
                  max={40}
                  step={1}
                  value={String(added[r])}
                  aria-label={`Additional ${r} roles`}
                  onChange={(e) => setAdded(r, e.target.value)}
                  className="h-7 w-12 rounded-md border border-solid border-pewter bg-white px-1.5 text-right text-[13px] tabular-nums text-black hover:border-iron focus:outline-none focus:ring-2 focus:ring-trustedBlue"
                />
              </span>
            ))}
            <button
              type="button"
              onClick={() => onAddedChange({ AAA: 0, A: 0, B: 0, C: 0 })}
              className="h-7 rounded-md border border-solid border-pewter bg-white px-2.5 text-[13px] font-medium text-black hover:bg-cotton"
            >
              Reset
            </button>
            <span className="text-steel">Never written back — the portfolio belongs to Airtable.</span>
          </div>
        </details>
      </Card>
    </section>
  );
}

/**
 * One figure. Label above value, so five of them fit a row at any realistic width — the earlier
 * inline "label value note" form wrapped onto a second line, which is exactly what must not happen
 * to the figures the tab exists to show. The note truncates rather than pushing the tile wider.
 */
function Kpi({
  label,
  value,
  unit,
  accent,
  testId,
  children,
}: {
  label: string;
  value: string;
  unit?: string;
  accent?: boolean;
  testId: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="min-w-0 border-r border-solid border-pearl px-4 py-3 last:border-r-0 max-[720px]:border-b"
    >
      <div className="truncate text-xs text-iron">{label}</div>
      <div
        className={cn(
          'mt-1 whitespace-nowrap text-[26px] font-semibold leading-8 tabular-nums',
          accent ? 'text-trustedBlue' : 'text-black'
        )}
      >
        {value}
        {unit ? <small className="text-sm font-normal text-iron"> {unit}</small> : null}
      </div>
      <div className="truncate text-xs tabular-nums text-steel">{children}</div>
    </div>
  );
}

/**
 * "Top-prio hotspot" — the cluster carrying the most high-priority (AAA + A) roles. Names the
 * cluster and states its high-prio counts on the second line; falls back to '—' when the live
 * cluster split is not available (seeded baseline). Weighting AAA slightly higher would only shift
 * a genuine tie; ties break by AAA count first, then A count, then alphabetical.
 */
function TopPrioKpi({ clusters }: { clusters?: readonly ClusterPortfolio[] }) {
  if (!clusters || clusters.length === 0) {
    return (
      <Kpi label="Top-prio hotspot" testId="kpi-top-prio" value="—">
        cluster split unavailable
      </Kpi>
    );
  }
  const ranked = [...clusters]
    .map((c) => ({ ...c, aaa: c.roles.AAA, a: c.roles.A, top: c.roles.AAA + c.roles.A }))
    .sort((x, y) => y.aaa - x.aaa || y.a - x.a || x.key.localeCompare(y.key));
  const winner = ranked[0];
  if (winner.top === 0) {
    return (
      <Kpi label="Top-prio hotspot" testId="kpi-top-prio" value="none">
        no AAA or A roles open
      </Kpi>
    );
  }
  return (
    <Kpi label="Top-prio hotspot" testId="kpi-top-prio" value={winner.key}>
      {winner.aaa > 0 ? `${winner.aaa} AAA` : ''}
      {winner.aaa > 0 && winner.a > 0 ? ' · ' : ''}
      {winner.a > 0 ? `${winner.a} A` : ''}
    </Kpi>
  );
}




